import type { ReactNode, SVGProps } from 'react';
import logoUrl from '@/assets/logo.png';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { size?: number };

/** Base for stroke icons. Decorative by default; pass aria-label to make one meaningful. */
function Stroke({ size = 18, strokeWidth = 2, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={rest['aria-label'] ? undefined : true}
      {...rest}
    >
      {children}
    </svg>
  );
}

function Filled({ size = 14, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden {...rest}>
      {children}
    </svg>
  );
}

/** An 8-ball, in its own colours (not currentColor): marks the pool table's card. The rim keeps it visible on dark cards. */
export const EightBallIcon = ({ size = 32, ...rest }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...rest}>
    <circle cx="12" cy="12" r="10.5" fill="#111" stroke="currentColor" strokeOpacity="0.45" />
    <circle cx="12" cy="11" r="6" fill="#fff" />
    <circle cx="12" cy="9" r="1.6" fill="none" stroke="#111" strokeWidth="1.3" />
    <circle cx="12" cy="12.6" r="2" fill="none" stroke="#111" strokeWidth="1.3" />
  </svg>
);

export const PlayIcon =(p: IconProps) => (
  <Filled {...p}>
    <polygon points="6 4 20 12 6 20" />
  </Filled>
);

export const PauseIcon = (p: IconProps) => (
  <Filled {...p}>
    <rect x="5" y="4" width="5" height="16" rx="1" />
    <rect x="14" y="4" width="5" height="16" rx="1" />
  </Filled>
);

export const StopIcon = (p: IconProps) => (
  <Filled {...p}>
    <rect x="5" y="5" width="14" height="14" rx="2" />
  </Filled>
);

export const RestartIcon = (p: IconProps) => (
  <Stroke strokeWidth={2.2} {...p}>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 3 3 9 9 9" />
  </Stroke>
);

export const CupIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
    <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" />
    <line x1="7" y1="2" x2="7" y2="4" />
    <line x1="11" y1="2" x2="11" y2="4" />
    <line x1="15" y1="2" x2="15" y2="4" />
  </Stroke>
);

/** A table seen from above, with pockets. Manage tables. */
export const TableTopIcon = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <circle cx="6" cy="9" r="0.8" fill="currentColor" />
    <circle cx="18" cy="9" r="0.8" fill="currentColor" />
    <circle cx="6" cy="15" r="0.8" fill="currentColor" />
    <circle cx="18" cy="15" r="0.8" fill="currentColor" />
  </Stroke>
);

/** Price tag. Manage condiments. */
export const TagIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8Z" />
    <circle cx="7.5" cy="7.5" r="1.2" />
  </Stroke>
);

export const PencilIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Stroke>
);

export const TrashIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
  </Stroke>
);

/** Three lines: opens the menu on small screens. */
export const MenuIcon = (p: IconProps) => (
  <Stroke {...p}>
    <line x1="4" y1="7" x2="20" y2="7" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="4" y1="17" x2="20" y2="17" />
  </Stroke>
);

export const ReceiptIcon = (p: IconProps) => (
  <Stroke strokeWidth={2.2} {...p}>
    <path d="M5 3h14v18l-3-2-2 2-2-2-2 2-2-2-3 2Z" />
    <line x1="9" y1="9" x2="15" y2="9" />
    <line x1="9" y1="13" x2="15" y2="13" />
  </Stroke>
);

/** One line forking into two: a bill split between teams. */
export const SplitIcon = (p: IconProps) => (
  <Stroke size={14} {...p}>
    <path d="M12 21v-7" />
    <path d="M12 14 6 8" />
    <path d="m12 14 6-6" />
    <polyline points="6 13 6 8 11 8" />
    <polyline points="13 8 18 8 18 13" />
  </Stroke>
);

export const CloseIcon = (p: IconProps) => (
  <Stroke strokeWidth={2.4} {...p}>
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </Stroke>
);

export const PlusIcon = (p: IconProps) => (
  <Stroke strokeWidth={2.4} {...p}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </Stroke>
);

export const MinusIcon = (p: IconProps) => (
  <Stroke strokeWidth={2.4} {...p}>
    <line x1="5" y1="12" x2="19" y2="12" />
  </Stroke>
);

export const SearchIcon = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.5" y2="16.5" />
  </Stroke>
);

export const HomeIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V21h14V9.5" />
  </Stroke>
);

export const UsersIcon = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="9" cy="8" r="4" />
    <path d="M2 21a7 7 0 0 1 14 0" />
    <path d="M16 3.5a4 4 0 0 1 0 7.5M22 21a7 7 0 0 0-4-6.3" />
  </Stroke>
);

/** A bound ledger book (the Ledgers menu). */
export const LedgerIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M5 5a2 2 0 0 1 2-2h12v15H7a2 2 0 0 0-2 2Z" />
    <path d="M5 20a2 2 0 0 0 2 2h12v-4" />
    <line x1="9" y1="7.5" x2="15" y2="7.5" />
    <line x1="9" y1="11.5" x2="15" y2="11.5" />
  </Stroke>
);

export const ChartIcon = (p: IconProps) => (
  <Stroke {...p}>
    <line x1="6" y1="20" x2="6" y2="12" />
    <line x1="12" y1="20" x2="12" y2="5" />
    <line x1="18" y1="20" x2="18" y2="9" />
  </Stroke>
);

export const SettingsIcon = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Stroke>
);

export const AlertIcon = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="7.5" x2="12" y2="13" />
    <line x1="12" y1="16.5" x2="12" y2="16.6" />
  </Stroke>
);

export const CheckIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M20 6 9 17l-5-5" />
  </Stroke>
);

export const ChevronDownIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="m6 9 6 6 6-6" />
  </Stroke>
);

export const ChevronLeftIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="m15 6-6 6 6 6" />
  </Stroke>
);

export const ChevronRightIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="m9 6 6 6-6 6" />
  </Stroke>
);

export const CalendarIcon = (p: IconProps) => (
  <Stroke {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Stroke>
);

export const ClockIcon = (p: IconProps) => (
  <Stroke {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Stroke>
);

export const EyeIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Stroke>
);

export const EyeOffIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-2.6 3.6" />
    <path d="M6.6 6.6C3.7 8.4 2 12 2 12s3.5 7 10 7c1.9 0 3.6-.6 5.1-1.5" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <line x1="3" y1="3" x2="21" y2="21" />
  </Stroke>
);

export const SignOutIcon = (p: IconProps) => (
  <Stroke {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </Stroke>
);

/** Brand logo (pool table + crossed cues). Same artwork as the favicon. */
export const LogoMark = ({ size = 34 }: { size?: number }) => (
  <img src={logoUrl} width={size} height={size} alt="" aria-hidden style={{ display: 'block' }} />
);
